import { useMemo, useState } from 'react';
import { Card, Typography, Tag, Button, Modal, Form, Input, InputNumber, Select, Tooltip, Space, FilterResetButton, Switch, Avatar, EmptyState, Tabs, theme, message, Drawer, Row, Col, StatCard, EntityAvatar, initialsOf, cn } from '../../ui';
import { Plus, Pencil, MapPin, Filter, Eraser, SlidersHorizontal, User, UserPlus, Phone, Mail, Warehouse as WarehouseIcon, TriangleAlert, PackageX, UserX, Boxes } from 'lucide-react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUrlString, useUrlList } from '../../hooks/useUrlState';
import { WarehouseActivityPage } from '../inventory/WarehouseActivityPage';
import { WarehouseManagersPage } from '../admin/WarehouseManagersPage';
import { TransportersPage } from '../inventory/TransportersPage';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { fmtQty, productLabel } from '../../lib/format';
import { usePermission, useAnyPermission } from '../../store/auth';
import { useDebounce } from '../../hooks/useDebounce';
import { TwDataTable, Pill, type DataTableColumn } from '../../components/TwDataTable';
import { LocationPicker } from '../../components/LocationPicker';
import { STATE_OPTIONS, STATE_NAME } from '../../constants/indianStates';
import { printedPhoneRules } from '../../lib/validators';
import { WarehouseReorderModal } from './WarehouseReorderModal';
import { useServerSort } from '../../hooks/useServerSort';

export function WarehousesPage() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [reorderFor, setReorderFor] = useState<{ id: string; code: string; name: string } | null>(null);
  const [managerFor, setManagerFor] = useState<any>(null); // warehouse whose manager(s) are shown in a drawer
  const [assignFor, setAssignFor] = useState<any>(null);   // warehouse for which the assign-manager modal is open
  const [expandedKeys, setExpandedKeys] = useState<any[]>([]); // which warehouse rows are expanded (controls the red highlight)
  // Creating / editing a warehouse needs WAREHOUSE.ADMIN (the backend enforces
  // it); plain WAREHOUSE.VIEW users get a read-only list.
  const canManage = usePermission('WAREHOUSE.ADMIN');
  // Setting per-warehouse minimum stock levels is an Admin/SA action (PRODUCT.EDIT).
  const canSetLevels = usePermission('PRODUCT.EDIT');
  const canAssignMgr = usePermission('WAREHOUSE.MANAGER.ASSIGN');
  // The transporter master rides along here — the carriers that collect from these locations.
  // Read is TRANSPORTER.VIEW; TRANSPORTER.ADMIN alone (never granted without VIEW today) still counts.
  const canSeeTransporters = useAnyPermission(['TRANSPORTER.VIEW', 'TRANSPORTER.ADMIN']);
  // Active tab from ?tab= — 'managers'/'transporters' only when the user holds the permission behind
  // them (else that tab isn't rendered, and a stale deep link falls back to the list).
  const rawTab = sp.get('tab');
  const tab = rawTab === 'activity' ? 'activity'
    : (rawTab === 'managers' && canAssignMgr) ? 'managers'
    : (rawTab === 'transporters' && canSeeTransporters) ? 'transporters'
    : 'list';

  // ---- Filters (resolved server-side) ----
  const [searchInput, setSearchInput] = useUrlString('q');
  const search = useDebounce(searchInput, 400);
  const [stateFilter, setStateFilter] = useUrlList('state');
  const [cityFilter, setCityFilter] = useUrlList('city');
  const [activeFilter, setActiveFilter] = useUrlString<'true' | 'false' | 'all'>('active', 'true');
  /*
   * WHICH TILE IS OPEN.
   *
   * The KPI row named three problems — sites under their minimum, the item lines under it, sites with
   * nobody accountable — and then made you find those rows yourself in a list of ten. A number that
   * states a problem is a question ("which ones?"), so each tile now answers it.
   *
   * It lives in the URL like every other filter on this page, so Back returns to what you were
   * looking at, and it narrows the ALREADY-FETCHED rows: the server query, its params and the facet
   * counts are untouched.
   */
  const [focus, setFocus] = useUrlString<'all' | 'low' | 'nomgr'>('focus', 'all');

  // Sorting is the backend's ORDER BY (see useServerSort) so every caller of this endpoint sees the
  // same order, and the sort survives a reload or a Back out of a row — it lives in the URL.
  // Manager + low-stock count are attached by a second query server-side, so they are NOT sortable.
  const { sortState, onSortChange, sort } = useServerSort();

  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (search.trim()) p.q = search.trim();
    if (stateFilter.length) p.state = stateFilter.join(',');
    if (cityFilter.length) p.city = cityFilter.join(',');
    if (activeFilter !== 'true') p.active = activeFilter; // backend defaults to active-only
    if (sort) p.sort = sort;
    return p;
  }, [search, stateFilter, cityFilter, activeFilter, sort]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['warehouses-full', params],
    queryFn: () => request<any[]>({ url: '/warehouses', params }),
    placeholderData: keepPreviousData,
  });

  // Facets + counts over ALL warehouses (incl. inactive), independent of current filters.
  const { data: facets } = useQuery({
    queryKey: ['warehouse-facets'],
    queryFn: () => request<any>({ url: '/warehouses/filter-options' }),
  });

  const rows = data ?? [];
  // Resolve the raw GST state codes ("27") to names ("Maharashtra") for the filter dropdown — the
  // value stays the code (so filtering still works), only the shown label is humanised. An unknown
  // code (bad legacy data like "MH") falls back to itself so it's still selectable + visible.
  const stateOptions = useMemo(
    () => (facets?.states ?? []).map((s: any) => ({ value: s.value, label: STATE_NAME.get(s.value) ?? s.value })),
    [facets],
  );
  const cityOptions = facets?.cities ?? [];
  const total = facets?.total ?? 0;

  // KPI roll-ups over the shown warehouses.
  const lowWarehouses = rows.filter((r: any) => Number(r.lowStockCount) > 0).length;
  const lowItems = rows.reduce((s: number, r: any) => s + Number(r.lowStockCount || 0), 0);
  const unassigned = rows.filter((r: any) => !(r.managers?.length)).length;

  const isLow = (r: any) => Number(r.lowStockCount) > 0;
  const hasManager = (r: any) => !!(r.managers?.length);
  // The list the table draws. `rows` stays the unfiltered set the tiles count from.
  const shownRows = useMemo(
    () => rows.filter((r: any) => (focus === 'low' ? isLow(r) : focus === 'nomgr' ? !hasManager(r) : true)),
    [rows, focus],
  );

  /* A tile's click REPLACES the focus rather than stacking onto it, so the number on the tile is
     exactly what the table then shows. Clicking the tile that is already open clears it. */
  const openFocus = (next: 'all' | 'low' | 'nomgr', expand?: boolean) => {
    const to = focus === next && !expand ? 'all' : next;
    setFocus(to);
    // "Low-stock ITEMS" counts lines, not sites — so opening it must put the lines themselves on
    // screen, not just the warehouses holding them. Expanding the matching rows does that; the
    // count would otherwise promise 3 and show 2.
    setExpandedKeys(expand && to === 'low' ? rows.filter(isLow).map((r: any) => r.id) : []);
  };

  const activeFilters =
    (search ? 1 : 0) + (stateFilter.length ? 1 : 0) +
    (cityFilter.length ? 1 : 0) + (activeFilter !== 'true' ? 1 : 0) + (focus !== 'all' ? 1 : 0);
  const clearFilters = () => {
    setSearchInput(''); setStateFilter([]); setCityFilter([]); setActiveFilter('true');
    setFocus('all'); setExpandedKeys([]);
  };

  const columns: DataTableColumn<any>[] = [
    {
      key: 'code', title: 'Code', dataIndex: 'code', sortable: true, alwaysVisible: true,
      // No glyph in front of the code. An identity mark earns its place by being DIFFERENT per row;
      // pinned to one warehouse icon it just reprinted the column header ten times and pushed the
      // codes — the thing people actually read down this column — out of alignment with the header.
      render: (v: string, r: any) => (
        <b className={cn('type-label-strong', r.isActive ? 'text-content' : 'text-muted')}>{v}</b>
      ),
    },
    {
      key: 'name', title: 'Name', dataIndex: 'name', sortable: true, alwaysVisible: true,
      render: (v: string, r: any) => (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          {v}
          {Number(r.lowStockCount) > 0 && <Pill color="red">{r.lowStockCount} low</Pill>}
        </span>
      ),
    },
    {
      // The GSTIN this warehouse supplies under — the mapping from Masters → GST Registrations, the
      // same one the edit form's dropdown sets. A NEW key (not the old hidden `gstNo`) so saved table
      // layouts reveal it: a column merely flipped to visible reaches nobody who has saved a layout.
      key: 'gstin', title: 'GSTIN', dataIndex: 'gstNo', sortable: true, width: 190,
      render: (v: string | null, r: any) => (r.gstRegistrationId && v
        ? <span className="font-mono type-label tabular-nums text-content">{v}</span>
        : (
          <Tooltip title="No GST registration mapped — orders fulfilled from here cannot be taxed. Pick one in Edit, or on Masters → GST Registrations.">
            <span><Pill color="red">Not mapped</Pill></span>
          </Tooltip>
        )),
    },
    {
      key: 'isMaster', title: 'Master', dataIndex: 'isMaster', sortable: true, width: 90,
      render: (v: boolean) => (v ? <Pill color="gold">Master</Pill> : null),
    },
    {
      key: 'city', title: 'City', dataIndex: 'city', sortable: true,
      render: (v: string) => (v
        ? (
          <span className="inline-flex items-center gap-1 rounded-pill bg-surface-3 px-2 py-0.5 type-label text-muted">
            <MapPin size={11} className="text-subtle" /> {v}
          </span>
        )
        : <span className="text-subtle">—</span>),
    },
    { key: 'stateCode', title: 'State', dataIndex: 'stateCode', sortable: true, width: 140,
      render: (v: string) => STATE_NAME.get(v) ?? v },
    { key: 'pincode', title: 'Pincode', dataIndex: 'pincode', defaultVisible: false },
    {
      key: 'geofence', title: 'Geofence (m)', dataIndex: 'geofenceRadiusM',
      sortable: true, width: 130, defaultVisible: false,
    },
    {
      key: 'manager', title: 'Manager', alwaysVisible: true, width: 190,
      render: (_: any, r: any) => {
        const mgrs = r.managers ?? [];
        if (!mgrs.length) {
          // Only roles with WAREHOUSE.MANAGER.ASSIGN can bind a manager; everyone else just sees the state.
          return canAssignMgr
            ? <Button variant="ghost" size="sm" icon={<UserPlus size={14} />} onClick={(e) => { e.stopPropagation(); setAssignFor(r); }}>Assign</Button>
            : (
              <span className="inline-flex items-center gap-1 rounded-pill border border-warning-500/35 bg-warning-500/12 px-2 py-0.5 type-label text-warning-600 dark:text-harvest-300">
                <UserX size={11} /> Unassigned
              </span>
            );
        }
        return (
          // No identity mark here: the manager is the same person on most rows, so the initials
          // repeated down the column were a constant glyph rather than something that varies.
          <a className="block min-w-0 truncate" onClick={(e) => { e.stopPropagation(); setManagerFor(r); }}>
            {mgrs[0].name}{mgrs.length > 1 ? ` +${mgrs.length - 1}` : ''}
          </a>
        );
      },
    },
    {
      key: 'isActive', title: 'Active', dataIndex: 'isActive', sortable: true, width: 90,
      render: (v: boolean) => v ? <Pill color="green">Yes</Pill> : <Pill>No</Pill>,
    },
    {
      key: 'actions', title: '', alwaysVisible: true, width: 150,
      render: (_: any, r: any) => (
        <Space wrap size={4}>
          {canManage && (
            <Tooltip label="Edit">
              <Button variant="text" size="sm" aria-label="Edit" icon={<Pencil size={16} />}
                onClick={(e) => { e.stopPropagation(); setEditing(r); setOpen(true); }} />
            </Tooltip>
          )}
          {canSetLevels && (
            <Tooltip label="Minimum stock levels">
              <Button variant="text" size="sm" aria-label="Minimum stock levels" icon={<SlidersHorizontal size={16} />}
                onClick={(e) => { e.stopPropagation(); setReorderFor({ id: String(r.id), code: r.code, name: r.name }); }} />
            </Tooltip>
          )}
          <Tooltip label="Map">
            <Button variant="text" size="sm" aria-label="Map" icon={<MapPin size={16} />}
              onClick={(e) => { e.stopPropagation(); window.open(`https://www.google.com/maps/search/?api=1&query=${r.latitude},${r.longitude}`, '_blank', 'noopener'); }} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Typography.Title level={4}>Warehouses</Typography.Title>
      <Tabs
        activeKey={tab}
        onChange={(k) => setSp({ tab: k }, { replace: true })}
        items={[{
          key: 'list',
          label: 'Warehouses',
          children: (
            <>
      {/* Header actions — moved out of the table toolbar, above the filters. */}
      {canManage && (
        <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
          <Button variant="primary" size="sm" icon={<Plus size={16} />}
            onClick={() => { setEditing(null); setOpen(true); }}>
            Add warehouse
          </Button>
        </div>
      )}
      <div className="mb-3.5 grid grid-cols-2 gap-3 md:grid-cols-4">
        {/* Every tile that CAN open rows does. One that counts zero is not offered as a control:
            a click whose only outcome is an empty table is not a filter. */}
        <StatCard
          tone="sky" icon={<WarehouseIcon size={18} />} label="Warehouses" value={total || rows.length}
          hint={focus === 'all' ? 'stocking locations on the master' : 'show all of them again'}
          onClick={rows.length ? () => openFocus('all') : undefined} active={focus === 'all'}
        />
        <StatCard
          tone={lowWarehouses ? 'danger' : 'neutral'} icon={<TriangleAlert size={18} />}
          label="Low-stock warehouses" value={lowWarehouses}
          hint={lowWarehouses ? 'below their minimum on at least one item' : 'every site is above its minimums'}
          onClick={lowWarehouses ? () => openFocus('low') : undefined}
          active={focus === 'low' && !expandedKeys.length}
        />
        <StatCard
          tone={lowItems ? 'danger' : 'neutral'} icon={<PackageX size={18} />}
          label="Low-stock items" value={lowItems}
          hint={lowItems ? 'open the sites and list the products' : 'products at a warehouse under their minimum'}
          onClick={lowItems ? () => openFocus('low', true) : undefined}
          active={focus === 'low' && !!expandedKeys.length}
        />
        <StatCard
          tone={unassigned ? 'warning' : 'brand'} icon={<UserX size={18} />}
          label="Without a manager" value={unassigned}
          hint={unassigned ? 'nobody is accountable for these sites' : 'every site has a manager'}
          onClick={unassigned ? () => openFocus('nomgr') : undefined} active={focus === 'nomgr'}
        />
      </div>

      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap items-center gap-2">{/* filter controls (Antd) */}
          <Input
            placeholder="Search code, name, city, GST" aria-label="Search code, name, city, GST"
            prefix={<Filter size={16} style={{ color: token.colorTextTertiary }} />}
            style={{ width: 250 }}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <Select
            mode="multiple" allowClear placeholder="City" style={{ minWidth: 160 }}
            maxTagCount={1} value={cityFilter} onChange={setCityFilter} options={cityOptions}
          />
          <Select
            mode="multiple" allowClear placeholder="State" style={{ minWidth: 130 }}
            maxTagCount={1} value={stateFilter} onChange={setStateFilter} options={stateOptions}
          />
          <Select
            style={{ width: 160 }}
            value={activeFilter}
            onChange={(v) => setActiveFilter(v)}
            options={[
              { value: 'true', label: 'Active only' },
              { value: 'false', label: 'Inactive only' },
              { value: 'all', label: 'All statuses' },
            ]}
          />
          {activeFilters > 0 && (
            <FilterResetButton size="md" count={activeFilters} onClick={clearFilters} />
          )}
      </div>

      <TwDataTable
        storageKey="warehouses"
        rowKey="id"
        loading={isLoading || isFetching}
        dataSource={shownRows}
        columns={columns}
        sortState={sortState}
        onSortChange={onSortChange}
        rowClassName={(r: any) => (Number(r.lowStockCount) > 0 && !expandedKeys.map(String).includes(String(r.id)) ? 'gb-row-low' : '')}
        expandable={{
          /* The "Low-stock items" tile counts PRODUCTS, so the panels it opens must list those
             products — not every product at the site with the short ones merely sorted to the top. */
          expandedRowRender: (r: any) => <WarehouseStock id={String(r.id)} lowOnly={focus === 'low'} />,
          expandedRowKeys: expandedKeys,
          onExpandedRowsChange: (keys) => setExpandedKeys(keys as any[]),
        }}
        onRow={(r) => ({
          // Clicking a row toggles its stock breakdown; editing is via the pencil action button.
          onClick: () => setExpandedKeys((keys) =>
            keys.map(String).includes(String(r.id)) ? keys.filter((k) => String(k) !== String(r.id)) : [...keys, r.id]),
        })}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        emptyText="No warehouses"
        toolbarLeft={
          /* A partial list must name what is narrowing it — an unexplained "2 of 10" reads as a bug. */
          <span className="flex flex-wrap items-center gap-2 text-sm text-muted">
            <span>
              {shownRows.length === total ? `${total} location${total === 1 ? '' : 's'}` : `${shownRows.length} of ${total} locations`}
              {activeFilters > 0 ? ` · ${activeFilters} filter${activeFilters === 1 ? '' : 's'}` : ''}
            </span>
            {focus !== 'all' && (
              <button
                type="button"
                onClick={() => openFocus('all')}
                className="inline-flex items-center gap-1 rounded-pill border border-brand-500/30 bg-brand-500/8 py-0.5 pl-2.5 pr-2 type-caption text-brand-700 transition-colors hover:bg-brand-500/15 dark:text-brand-300"
              >
                {focus === 'low'
                  ? (expandedKeys.length
                    ? `showing the ${lowItems} low-stock item${lowItems === 1 ? '' : 's'}`
                    : 'showing low-stock sites only')
                  : 'showing sites with no manager'}
                <Eraser size={12} />
              </button>
            )}
          </span>
        }
      />
            </>
          ),
        }, {
          key: 'activity',
          label: 'Activity',
          children: <WarehouseActivityPage embedded />,
        },
        // Manager assignment (was the standalone Admin → Warehouse Managers page) — shown to WAREHOUSE.MANAGER.ASSIGN.
        ...(canAssignMgr ? [{
          key: 'managers',
          label: 'Managers',
          children: <WarehouseManagersPage embedded />,
        }] : []),
        // Transporter master (was the standalone Warehouse → Transporters page).
        ...(canSeeTransporters ? [{
          key: 'transporters',
          label: 'Transporters',
          children: <TransportersPage embedded />,
        }] : [])]}
      />

      <WarehouseFormModal open={open} record={editing} onClose={() => { setOpen(false); setEditing(null); }} />

      <WarehouseReorderModal warehouse={reorderFor} onClose={() => setReorderFor(null)} />

      <Drawer
        open={!!managerFor}
        onClose={() => setManagerFor(null)}
        width={420}
        title={managerFor ? `Warehouse manager — ${managerFor.code}` : 'Warehouse manager'}
      >
        {(managerFor?.managers ?? []).length === 0 ? (
          <EmptyState title="No manager assigned to this warehouse." />
        ) : (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {(managerFor?.managers ?? []).map((m: any) => (
              <Card key={m.id}>
                <Space align="start">
                  <Avatar size={44} src={m.photoUrl || undefined} fallback={<User size={16} />} />
                  <div>
                    <div><b>{m.name}</b> {m.role && <Tag style={{ marginLeft: 4 }}>{m.role}</Tag>}</div>
                    {m.phone && <div style={{ marginTop: 4 }}><Phone size={16} /> <a href={`tel:${m.phone}`}>{m.phone}</a></div>}
                    {m.email && <div><Mail size={16} /> <a href={`mailto:${m.email}`}>{m.email}</a></div>}
                  </div>
                </Space>
              </Card>
            ))}
          </Space>
        )}
        {canAssignMgr && (
          <Space direction="vertical" style={{ width: '100%', marginTop: 16 }}>
            <Button variant="primary" block icon={<UserPlus size={16} />} onClick={() => { const w = managerFor; setManagerFor(null); setAssignFor(w); }}>
              Assign {(managerFor?.managers ?? []).length ? 'another manager' : 'manager'}
            </Button>
            <Button block onClick={() => { setManagerFor(null); navigate('/admin/warehouse-managers'); }}>
              Manage assignments
            </Button>
          </Space>
        )}
      </Drawer>

      <AssignManagerModal warehouse={assignFor} onClose={() => setAssignFor(null)} />
    </div>
  );
}

// Quick "assign a Warehouse Manager to this warehouse" modal, opened from the Manager column / drawer.
// Gated upstream by WAREHOUSE.MANAGER.ASSIGN; the backend re-checks the same permission.
function AssignManagerModal({ warehouse, onClose }: { warehouse: any | null; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const open = !!warehouse;
  const [userId, setUserId] = useState<string>();
  const [isPrimary, setIsPrimary] = useState(true);
  const [note, setNote] = useState('');

  // Users that hold the Warehouse Manager role — the only ones eligible to be assigned.
  const { data: wmUsers, isLoading } = useQuery({
    queryKey: ['wm-users'],
    queryFn: () => requestAllPages('/users', { role: 'WAREHOUSE_MANAGER' }),
    enabled: open,
  });
  const assignedIds = new Set((warehouse?.managers ?? []).map((m: any) => String(m.id)));
  const options = (wmUsers?.data ?? [])
    .filter((u: any) => !assignedIds.has(String(u.id)))
    .map((u: any) => ({ value: String(u.id), label: `${u.fullName} · ${u.mobile}` }));

  const close = () => { setUserId(undefined); setIsPrimary(true); setNote(''); onClose(); };

  const assign = useConfirmedMutation({ title: "Assign this manager to the warehouse?", content: "They get warehouse access and start receiving its low-stock alerts.", okText: "Yes, assign" }, {
    mutationFn: () => request({ url: '/warehouse-assignments', method: 'POST', data: { userId, warehouseId: String(warehouse.id), isPrimary, note: note.trim() || undefined } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warehouses-full'] });
      qc.invalidateQueries({ queryKey: ['warehouse-facets'] });
      qc.invalidateQueries({ queryKey: ['warehouse-assignments'] });
      message.success('Warehouse manager assigned');
      close();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to assign manager'),
  });

  return (
    <Modal
      open={open} onOpenChange={(o) => { if (!o) close(); }}
      title={warehouse ? `Assign manager · ${warehouse.code} — ${warehouse.name}` : 'Assign manager'}
      footer={<><Button variant="default" onClick={close}>Cancel</Button><Button variant="primary" loading={assign.isPending} disabled={!userId} onClick={() => assign.mutate()}>Assign</Button></>}
    >
      <Form layout="vertical">
        <Form.Item label="Warehouse Manager" required>
          <Select
            showSearch optionFilterProp="label" placeholder="Select a Warehouse Manager"
            style={{ width: '100%' }} loading={isLoading} value={userId} onChange={setUserId} options={options}
            notFoundContent={isLoading ? 'Loading…' : 'No users with the Warehouse Manager role'}
          />
        </Form.Item>
        <Form.Item label="Primary manager" tooltip="The main point of contact for this warehouse.">
          <Switch checked={isPrimary} onCheckedChange={setIsPrimary} />
        </Form.Item>
        <Form.Item label="Note (optional)">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. interim cover" />
        </Form.Item>
      </Form>
      <Typography.Paragraph type="secondary" className="text-xs" style={{ marginBottom: 0 }}>
        Only users with the <b>Warehouse Manager</b> role appear here.{' '}
        <a onClick={() => { close(); navigate('/admin/warehouse-managers'); }}>Open manager admin</a> for full control.
      </Typography.Paragraph>
    </Modal>
  );
}

// Product-wise on-hand stock for a warehouse — lazy-loaded when its row is expanded.
//
// The panel spans the register's full width, so the columns have to EARN it: the product carries its
// own photo, the part that cannot be sold gets its own column, and the widest column is a bar whose
// TRACK IS THE MINIMUM — full means the minimum is met, short means it is not. Four bare columns in
// the same space left the name marooned on the left edge, the figures pinned a thousand pixels away
// on the right, and a flat pink wash over any low line.
function WarehouseStock({ id, lowOnly }: {
  id: string;
  /**
   * Opened from the low-stock drill: list the products the tile counted, and nothing else.
   *
   * The tile says a number and the chip beside the filter says "showing the N low-stock items", but
   * the panels listed every product at each site with the short ones sorted first. At Pune that is
   * one short product among fourteen — so answering "which ones?" meant reading every row and
   * working out, product by product, which had a minimum at all and which was under it. A drill-down
   * has to show the set it counted.
   */
  lowOnly?: boolean;
}) {
  /* Escape hatch, per panel. The rest of the site's stock is one click away and the line that
     offers it says how many products it is adding back, so nothing is hidden silently. */
  const [showAll, setShowAll] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['warehouse-stock', id],
    queryFn: () => request<any[]>({ url: `/warehouses/${id}/stock` }),
  });

  // Every figure below is a field on the API row or a subtraction of two of them: on hand splits
  // into sellable (status AVAILABLE + not expired) and non-sellable (expired / QC-hold / blocked) —
  // the order screens can only promise the sellable part — and a low line's shortfall is what it
  // would take to get back to the minimum.
  const lines = useMemo(() => (data ?? []).map((r: any) => {
    const onHand = Number(r.available) || 0;
    const sellable = Number(r.sellable ?? r.available) || 0;
    const min = Number(r.minLevel) || 0;
    // A SKU that only re-spells the name ("AGN" + "AGN 500ml") printed the product twice on one
    // line; productLabel keeps whichever label is the whole truth and drops the echo.
    const { primary, secondary } = productLabel(r.productName, r.sku);
    return {
      ...r, onHand, sellable, min, primary, secondary,
      nonSellable: Math.max(0, onHand - sellable),
      low: min > 0 && onHand <= min,
      short: Math.max(0, min - onHand),
      // How much of the minimum this line covers, capped at 100. Earlier the bar was scaled per row
      // to max(on hand, minimum): the fill and the minimum tick then sat somewhere different on every
      // row and a well-stocked line read as "11.5x the minimum" — a number you had to decode. The
      // minimum IS the track now, so there is one denominator, the same on every row, and nothing to
      // read but "full or short".
      pctOfMin: min > 0 ? Math.min(100, Math.round((onHand / min) * 100)) : null,
    };
  })
    // A site can hold 38 products; the ones under their minimum are why the panel was opened, so
    // they lead. Everything else keeps the API's alphabetical order.
    .sort((a: any, b: any) => (b.low ? 1 : 0) - (a.low ? 1 : 0)), [data]);
  const lowCount = lines.filter((l: any) => l.low).length;
  const nsCount = lines.filter((l: any) => l.nonSellable > 0).length;
  /* What this panel lists. Narrowed only while the drill is on AND the reader has not asked for the
     rest — and never to an empty table, which would read as "no stock at this site". */
  const narrowed = !!lowOnly && !showAll && lowCount > 0;
  const shown = narrowed ? lines.filter((l: any) => l.low) : lines;
  const hidden = lines.length - shown.length;

  const TH = 'px-3 py-2 type-overline text-muted';
  return (
    <div>
      {/* Says what the panel is and which lines need a decision, so no row has to shout it. */}
      <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="inline-flex items-center gap-1.5 type-label-strong text-content">
          <Boxes size={14} className="text-brand-600 dark:text-brand-400" />
          Stock on hand
        </span>
        {!isLoading && (
          <span className="type-caption text-subtle">
            {narrowed
              ? `${shown.length} of ${lines.length} product${lines.length === 1 ? '' : 's'}`
              : `${lines.length} product${lines.length === 1 ? '' : 's'}`}
          </span>
        )}
        {lowCount > 0 && <Pill color="red"><TriangleAlert size={11} />{lowCount} below minimum</Pill>}
        {nsCount > 0 && <Pill color="orange"><PackageX size={11} />{nsCount} carrying non-sellable stock</Pill>}
      </div>

      {/* A bordered card on the tinted strip — one separator, and it floats clear of the parent rows. */}
      <div className="overflow-hidden rounded-control border border-line bg-surface">
        {/* A long list scrolls INSIDE the card (with its headers pinned) instead of pushing the rest
            of the register off the screen. */}
        <div className="max-h-[26rem] overflow-auto">
          <table className="w-full min-w-[720px] table-fixed border-collapse text-sm">
            <colgroup>
              <col style={{ width: '26%' }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: '34%' }} />
              <col style={{ width: 90 }} />
            </colgroup>
            <thead className="gb-thead-tint sticky top-0 z-10 bg-surface-2">
              <tr>
                <th className={`${TH} text-left`}>Product</th>
                <th className={`${TH} text-right`}>On hand</th>
                <th className={`${TH} text-right`}>Non-sellable</th>
                <th className={`${TH} text-right`}>Minimum</th>
                <th className={`${TH} text-left`}>Cover vs minimum</th>
                <th className={`${TH} text-right`}>Batches</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                [0, 1, 2].map((i) => (
                  <tr key={i} className="border-t border-line">
                    <td colSpan={6} className="px-3 py-3"><div className="h-5 animate-pulse rounded bg-surface-3" /></td>
                  </tr>
                ))
              ) : shown.length ? (
                shown.map((l: any) => (
                  <tr
                    key={l.productId}
                    className={cn(
                      'border-t border-line transition-colors',
                      // Mutually exclusive branches: two utilities setting `background` would resolve
                      // by stylesheet order, not class order, and the hover would silently do nothing.
                      l.low ? 'bg-danger-500/6 hover:bg-danger-500/12' : 'hover:bg-surface-2',
                    )}
                  >
                    {/* A low line is marked by a rail on its own row instead of a wash across the whole
                        strip; the transparent rail elsewhere keeps every product name on one x. */}
                    <td className={cn('border-l-2 px-3 py-2', l.low ? 'border-danger-500' : 'border-transparent')}>
                      <div className="flex min-w-0 items-center gap-2.5">
                        {/* The product's own cover photo; where there is none, initials from the NAME on
                            a hue hashed from the variant label (a pack size makes nonsense initials —
                            "AGN 500ml" came out as "A5"). */}
                        <EntityAvatar name={l.primary} src={l.imageUrl} icon={initialsOf(l.productName)} square size={36} />
                        <div className="min-w-0">
                          <div className="truncate text-content">{l.primary}</div>
                          {l.secondary && <div className="truncate type-caption text-subtle">{l.secondary}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className={cn('type-body-strong tabular-nums', l.low ? 'text-danger-600' : 'text-content')}>{fmtQty(l.onHand)}</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {l.nonSellable > 0 ? (
                        <Tooltip title="Expired, on QC-hold or blocked — on the shelf, but it cannot be sold or dispatched. Listed in Stock & Valuation → Non-sellable.">
                          <span className="cursor-help tabular-nums text-warning-600 dark:text-harvest-300">{fmtQty(l.nonSellable)}</span>
                        </Tooltip>
                      ) : (
                        <span className="text-subtle">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {l.min > 0 ? (
                        <>
                          <div className="tabular-nums text-muted">{fmtQty(l.min)}</div>
                          {l.low && (
                            <div className="type-caption text-danger-600">{l.short > 0 ? `${fmtQty(l.short)} short` : 'at minimum'}</div>
                          )}
                        </>
                      ) : (
                        <span className="text-subtle">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {/* ONLY THE EXCEPTION GETS A BAR, and its whole track is the minimum — so a
                          bar means "short by this much", with one denominator and nothing to
                          interpret. Drawn on every line instead, it was 21 identical full bars down
                          the column: a mark that never varies says only "this row exists". A line
                          that is fine, and a line with no minimum to measure against, say so in
                          words. */}
                      {l.low ? (
                        <Tooltip
                          block
                          title={`The whole bar is this site's minimum of ${fmtQty(l.min)}; on hand is ${fmtQty(l.onHand)}, so ${fmtQty(l.short)} short of it.`}
                        >
                          <span className="block cursor-help">
                            <span className="block h-1.5 w-full overflow-hidden rounded-pill bg-surface-3">
                              <span className="block h-full rounded-pill bg-danger-500/85" style={{ width: `${l.pctOfMin}%` }} />
                            </span>
                            <span className="mt-1.5 block type-caption text-danger-600">
                              {l.pctOfMin}% of the {fmtQty(l.min)} minimum
                            </span>
                          </span>
                        </Tooltip>
                      ) : (
                        <span className="type-caption text-subtle">{l.min > 0 ? 'minimum met' : 'no minimum set'}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted">{l.batches}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-subtle">No stock in this warehouse</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* NAME THE GAP. A narrowed panel says what it is leaving out and offers it back, so the
          reader is never left wondering whether a site really holds two products or whether the
          screen is hiding twelve. Amber, like every other gap line in the app. */}
      {!isLoading && narrowed && hidden > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-2 inline-flex items-center gap-1.5 rounded-control border border-harvest-500/30 bg-harvest-500/8 px-3 py-1.5 type-caption text-harvest-700 transition-colors hover:bg-harvest-500/15 dark:text-harvest-300"
        >
          <TriangleAlert size={12} />
          {hidden} more product{hidden === 1 ? '' : 's'} here {hidden === 1 ? 'is' : 'are'} at or above {hidden === 1 ? 'its' : 'their'} minimum — show the whole site
        </button>
      )}
      {!isLoading && !!lowOnly && showAll && lowCount > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(false)}
          className="mt-2 inline-flex items-center gap-1.5 rounded-control border border-line bg-surface-2 px-3 py-1.5 type-caption text-muted transition-colors hover:bg-surface-3"
        >
          <PackageX size={12} />
          Back to the {lowCount} short product{lowCount === 1 ? '' : 's'}
        </button>
      )}
    </div>
  );
}

function WarehouseFormModal({ open, onClose, record }: { open: boolean; onClose: () => void; record?: any }) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const isEdit = !!record;
  // The supplying GSTIN is picked from the GST Registrations master, never typed — a typed GSTIN here
  // and the mapping on that page used to be two separate records that drifted apart.
  const canMapGst = usePermission('GST.REGISTRATION.ADMIN');
  const canSeeGst = useAnyPermission(['GST.REGISTRATION.VIEW', 'GST.REGISTRATION.ADMIN']);
  const { data: registrations, isLoading: regsLoading } = useQuery({
    queryKey: ['gst-registrations', 'picker'],
    queryFn: () => request<any[]>({ url: '/gst/registrations' }),
    enabled: open && canSeeGst,
  });
  const whState = Form.useWatch('stateCode', form);
  const pickedRegId = Form.useWatch('gstRegistrationId', form);
  const pickedReg = (registrations ?? []).find((r: any) => String(r.id) === String(pickedRegId ?? ''));
  const regOptions = (registrations ?? [])
    .slice()
    // Registrations in the warehouse's own state first — that is almost always the right one.
    .sort((a: any, b: any) => Number(b.stateCode === whState) - Number(a.stateCode === whState) || Number(b.isPrimary) - Number(a.isPrimary))
    .map((r: any) => ({
      value: String(r.id),
      label: `${r.gstin} · ${r.stateName || STATE_NAME.get(r.stateCode) || r.stateCode}${r.isPrimary ? ' · Head office' : ''}${r.isVerified ? '' : ' · not verified'}`,
    }));

  // Seed via initialValues (record is available synchronously at open time) + a per-target key on the
  // <Form> below, so the remounting Form store is seeded correctly at render each open — no
  // setFieldsValue-vs-remount race (blank fields) and no stale code/id leaking from a prior open.
  const initialValues = record
    // Drop the list-only derived fields so the form never echoes them back to the update (the backend
    // also strips non-columns, but a clean payload keeps the activity-log diff tidy).
    ? (() => { const { managers, lowStockCount, gstNo, ...w } = record; return { ...w, gstRegistrationId: record.gstRegistrationId ? String(record.gstRegistrationId) : null, latitude: Number(record.latitude), longitude: Number(record.longitude) }; })()
    : { geofenceRadiusM: 200, isActive: true, isMaster: false };

  const save = useConfirmedMutation({ title: isEdit ? "Save this warehouse?" : "Create this warehouse?", okText: "Yes, save" }, {
    mutationFn: (vals: any) => {
      const payload = { ...vals, latitude: String(vals.latitude), longitude: String(vals.longitude) };
      delete payload.gstNo;
      if (canMapGst) payload.gstRegistrationId = vals.gstRegistrationId || null;
      else delete payload.gstRegistrationId;
      return isEdit
        ? request({ url: `/warehouses/${record.id}`, method: 'PUT', data: payload })
        : request({ url: '/warehouses', method: 'POST', data: payload });
    },
    onSuccess: () => {
      message.success(isEdit ? 'Updated' : 'Created');
      qc.invalidateQueries({ queryKey: ['warehouses-full'] });
      qc.invalidateQueries({ queryKey: ['warehouse-facets'] });
      qc.invalidateQueries({ queryKey: ['warehouses'] });
      // The GST Registrations page shows the same mapping.
      qc.invalidateQueries({ queryKey: ['warehouses-for-gst'] });
      qc.invalidateQueries({ queryKey: ['gst-registrations'] });
      qc.invalidateQueries({ queryKey: ['gst-health'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title={isEdit ? `Edit · ${record?.code}` : 'New warehouse'}
      width={780}
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>{isEdit ? 'Update' : 'Create'}</Button></>}>
      <Form form={form} key={record ? `edit-${record.id}` : 'create'} initialValues={initialValues} layout="vertical" onFinish={(v) => save.mutate(v)}>
        <Row gutter={[12, 12]}>
          {/* Row 1 — basics (two columns) */}
          <Col xs={24} sm={12}>
            <Form.Item name="code" label="Code" rules={[{ required: true }]}
              extra={isEdit ? 'Unique identifier. Renaming updates it everywhere; opening-stock CSVs must then use the new code.' : undefined}>
              <Input placeholder="WH-XYZ" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="isMaster" label="Master warehouse" valuePropName="checked"
              tooltip="The single stock-origin point — stock is added directly here; all other warehouses receive it via transfer. Turning this on moves the master to this warehouse.">
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="geofenceRadiusM" label="Geofence radius (m)">
              <InputNumber style={{ width: '100%' }} min={50} step={50} />
            </Form.Item>
          </Col>

          {/* Map picker (full width) */}
          <Col span={24}>
            <LocationPicker fields={{ address: 'addressLine1', state: 'stateCode' }} required />
          </Col>

          {/* Address & details — two columns */}
          <Col xs={24} sm={12}>
            <Form.Item name="addressLine1" label="Address line 1" rules={[{ required: true }]}><Input /></Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="addressLine2" label="Address line 2"><Input /></Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="city" label="City" rules={[{ required: true }]}><Input /></Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="stateCode" label="State" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Select state"
                options={STATE_OPTIONS}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="pincode" label="Pincode" rules={[{ required: true }]}>
              <Input maxLength={6} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="gstRegistrationId" label="Supplies under GSTIN"
              extra={
                !canSeeGst ? 'Set on Masters → GST Registrations.'
                  : !regsLoading && !(registrations ?? []).length
                    ? <>No GST registration yet — add one on <a className="text-brand-600 underline" href="/masters/gst-registrations">Masters → GST Registrations</a>.</>
                    : pickedReg && whState && pickedReg.stateCode !== whState
                      ? <span className="text-warning-600">This GSTIN is registered in {pickedReg.stateName || pickedReg.stateCode}, but the warehouse is in {STATE_NAME.get(whState) ?? whState}.</span>
                      : 'Picked from Masters → GST Registrations, which shows the same mapping. Printed on documents from this warehouse and used for their tax. Empty = not mapped: orders from here cannot be taxed.'
              }>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={canSeeGst ? 'Not mapped' : (record?.gstNo || 'Not mapped')}
                loading={regsLoading}
                disabled={!canMapGst}
                options={regOptions}
              />
            </Form.Item>
          </Col>

          {/* ── What documents print ──────────────────────────────────────────────────────
              A proforma / invoice / credit note raised from this warehouse heads with the
              address above and this contact line, so the dealer reaches the godown that
              packed the consignment. Blank fields fall back to the company's. */}
          <Col span={24}>
            <div className="mt-1 border-t border-line pt-3">
              <div className="type-section-title text-content">On documents</div>
              <div className="type-label text-muted">
                Proformas, invoices and credit notes raised from this warehouse print the address above,
                with these details. Anything left blank falls back to the company profile.
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="phone" label="Phone" rules={printedPhoneRules}>
              <Input placeholder="+91 (20) 4064 6022" maxLength={20} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="email" label="Email"
              rules={[{ type: 'email', message: 'Enter a valid email address' }]}>
              <Input placeholder="pune.depot@greenbaybio.com" maxLength={120} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="fertiliserLicenceNo" label="Fertiliser licence no."
              tooltip="Licences are issued per premises, so they belong to this warehouse. Each prints on the document only when filled.">
              <Input maxLength={60} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="pesticideLicenceNo" label="Pesticide licence no."><Input maxLength={60} /></Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="seedLicenceNo" label="Seed licence no."><Input maxLength={60} /></Form.Item>
          </Col>

          {/* Coordinates come from the map picker above — kept as hidden required fields. */}
          <Form.Item name="latitude" hidden rules={[{ required: true, message: 'Pick a location on the map' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="longitude" hidden rules={[{ required: true, message: 'Pick a location on the map' }]}>
            <Input />
          </Form.Item>
        </Row>
      </Form>
    </Modal>
  );
}
